<script>
  import { createEventDispatcher } from "svelte";
  import Popup from "./Popup.svelte";
  import { tourFunctions } from "../tour-functions";
  import i18n from "./i18n/i18n.js";

  export let tour;
  export let visible = true;
  /**
   * Invoked only when the visitor presses Start Tour and the routes block is shown.
   * Pass null for “All” (default tour, do not open personalization), or a featured route object.
   */
  export let onApplyFeaturedRoute = () => {};

  const dispatch = createEventDispatcher();

  $: heading = tour?.introPopup?.heading ?? "";
  $: logoUrl = tour?.introPopup?.logoUrl ?? "";
  $: logoWidth = tour?.introPopup?.logoWidth ?? null;
  $: logoHeight = tour?.introPopup?.logoHeight ?? null;
  $: allScenesRouteTitle =
    tour?.introPopup?.allScenesRouteTitle ?? $i18n.t("tour.allScenesRouteTitle");
  $: allScenesRouteDescription =
    tour?.introPopup?.allScenesRouteDescription ?? $i18n.t("tour.allScenesRouteDescription");
  $: allScenesRouteThumbUrl = tour?.introPopup?.allScenesRouteThumbUrl ?? "";
  $: contentHtml = tour?.introPopup?.contentHtml ?? "";
  $: afterRoutesContentHtml = tour?.introPopup?.afterRoutesContentHtml ?? "";
  $: videoEmbedUrl = tour?.introPopup?.videoEmbedUrl ?? "";
  $: featuredRouteOrder = tour?.introPopup?.featuredRouteOrder ?? [];
  $: featuredRoutes = (tour?.routes ?? [])
    .filter((r) => r.featured)
    .slice()
    .sort((a, b) => {
      const aIndex = featuredRouteOrder.indexOf(a.id);
      const bIndex = featuredRouteOrder.indexOf(b.id);
      const safeAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
      const safeBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
      if (safeAIndex !== safeBIndex) return safeAIndex - safeBIndex;
      return (a?.title || "").localeCompare(b?.title || "");
    });
  $: showRoutesSection =
    tour?.introPopup?.showRoutes &&
    tour?.features?.includes("routes") &&
    featuredRoutes.length > 0;
  $: routeScrollbarThumb = getContrastScrollbarColor(
    tour?.theme?.popupBgColor || tour?.theme?.primaryColor || "#000000",
  );

  const parseContent = tourFunctions.parseContent || ((s) => (s || "").replaceAll("[", "<").replaceAll("]", ">"));

  function getContrastScrollbarColor(color) {
    const c = (color || "").trim().toLowerCase();
    if (!c) return "rgba(255, 255, 255, 0.72)";

    const hex = c.startsWith("#") ? c.slice(1) : c;
    if (/^[0-9a-f]{3}$/.test(hex) || /^[0-9a-f]{6}$/.test(hex)) {
      const normalized = hex.length === 3
        ? hex.split("").map((ch) => ch + ch).join("")
        : hex;
      const r = parseInt(normalized.slice(0, 2), 16);
      const g = parseInt(normalized.slice(2, 4), 16);
      const b = parseInt(normalized.slice(4, 6), 16);
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return luminance > 160
        ? "rgba(0, 0, 0, 0.72)"
        : "rgba(255, 255, 255, 0.72)";
    }

    return "rgba(255, 255, 255, 0.72)";
  }

  function handleStartTour(routeSelection = null) {
    if (showRoutesSection) {
      onApplyFeaturedRoute(routeSelection);
    }
    dispatch("close");
  }
</script>

<Popup
  {visible}
  ariaLabel="Tour intro"
  width={tour?.introPopup?.width || "80%"}
  on:close={() => dispatch("close")}
>
  <div
    class="intro-popup"
    class:has-routes={showRoutesSection}
    style={`--route-scrollbar-thumb: ${routeScrollbarThumb};`}
  >
    <div class="intro-main">
      {#if logoUrl}
        <div class="intro-popup-logo-wrap">
          <img
            src={logoUrl}
            alt={heading ? `${heading} logo` : "Intro logo"}
            class="intro-popup-logo"
            style={`width: ${logoWidth ? `${logoWidth}px` : "auto"}; height: ${logoHeight ? `${logoHeight}px` : "auto"};`}
            loading="lazy"
          />
        </div>
      {/if}
      {#if heading}
        <h2 class="intro-popup-heading">{heading}</h2>
      {/if}
      {#if contentHtml}
        <div class="intro-popup-content" class:has-video={videoEmbedUrl}>
          {@html parseContent(contentHtml)}
        </div>
      {/if}

      {#if videoEmbedUrl}
        <div class="intro-popup-video">
          <div class="video-wrapper">
            <iframe
              src={videoEmbedUrl}
              title="Intro video"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>
        </div>
      {/if}
    </div>

    {#if showRoutesSection}
      <aside class="intro-routes">
        <h4 class="intro-routes-heading">Featured Tour Routes</h4>
        <p class="routes-description">
          Choose a route and start the tour
        </p>
        <div class="routes-grid">
          <article class="route-card route-card-all">
            {#if allScenesRouteThumbUrl}
              <img
                class="route-image"
                src={allScenesRouteThumbUrl}
                alt="All scenes thumbnail"
                loading="lazy"
              />
            {/if}
            <div class="route-card-content">
              <h5 class="route-title">{allScenesRouteTitle}</h5>
              <p class="route-description route-description-always">
                {allScenesRouteDescription}
              </p>
              <button
                type="button"
                class="route-start-button route-start-button-always"
                on:click={() => handleStartTour(null)}
              >
                Start Tour
              </button>
            </div>
          </article>
          {#each featuredRoutes as route}
            <article class="route-card">
              {#if route.thumbUrl}
                <img
                  class="route-image"
                  src={route.thumbUrl}
                  alt={route.thumbAltText || `${route.title} thumbnail`}
                  loading="lazy"
                />
              {:else}
                <div class="route-image-placeholder" aria-hidden="true">
                  No image
                </div>
              {/if}
              <div class="route-card-content">
                <h5 class="route-title">{route.title}</h5>
                {#if route.description}
                  <p class="route-description">{route.description}</p>
                {/if}
                <button
                  type="button"
                  class="route-start-button route-start-button-hover"
                  on:click={() => handleStartTour(route)}
                >
                  Start Tour
                </button>
              </div>
            </article>
          {/each}
        </div>
      </aside>
    {/if}

    {#if showRoutesSection && afterRoutesContentHtml}
      <div class="intro-post-content">
        {@html parseContent(afterRoutesContentHtml)}
      </div>
    {/if}

    {#if !showRoutesSection}
      <div class="intro-popup-actions">
        <button type="button" class="start-tour-button" on:click={() => handleStartTour(null)}>
          Start Tour
        </button>
      </div>
    {/if}
  </div>
</Popup>

<style>
  .intro-popup {
    padding-top: 2rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 100%;
  }

  .intro-main {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .intro-popup-heading {
    margin: 0;
    padding: 0;
    font-size: 1.5rem;
    font-weight: 700;
    font-family: var(--font-heading), sans-serif;
    position: sticky;
    top: 0;
    z-index: 3;
    background: var(--popup-bg-color);
  }

  .intro-popup-logo-wrap {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    margin-bottom: 1rem;
  }

  .intro-popup-logo {
    max-width: 100%;
    object-fit: contain;
    display: block;
  }

  .intro-popup-content {
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  .intro-popup-content :global(p) {
    margin-bottom: 0.75rem;
  }

  .intro-popup-content :global(p:last-child) {
    margin-bottom: 0;
  }

  .intro-post-content {
    line-height: 1.6;
  }

  .intro-post-content :global(p) {
    margin-bottom: 0.75rem;
  }

  .intro-post-content :global(p:last-child) {
    margin-bottom: 0;
  }

  .intro-popup-video {
    width: 100%;
  }

  .video-wrapper {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; /* 16:9 */
    height: 0;
    overflow: hidden;
    border-radius: 0.85rem;
  }

  .video-wrapper iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .intro-popup-actions {
    margin-top: 0.5rem;
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .start-tour-button {
    appearance: none;
    padding: 0.75rem 1.5rem;
    font-size: 1.25rem;
    font-weight: 700;
    font-family: var(--font-heading), sans-serif;
    color: var(--popup-text-color);
    background: rgba(var(--theme-color-rgb), 0.9);
    border: 2px solid var(--popup-text-color);
    border-radius: 4px;
    cursor: pointer;
  }

  .start-tour-button:hover {
    background: rgba(var(--theme-color-rgb), 1);
  }

  .start-tour-button:focus-visible {
    outline: 4px solid var(--focus-color);
    outline-offset: 2px;
  }

  /* Match personalization (ThumbsGrid) route pills */
  .intro-routes {
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  .intro-routes-heading {
    margin: 0 0 0.5rem 0;
    font-size: 1.25rem;
    font-weight: 900;
    font-family: var(--font-heading), sans-serif;
    color: var(--popup-text-color);
  }

  .routes-description {
    font-family: var(--font-body), sans-serif;
    font-size: 0.95rem;
    color: var(--popup-text-color);
    opacity: 0.82;
    margin: 0 0 0.5rem 0;
    line-height: 1.4;
  }

  .routes-grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    align-content: start;
    gap: 1rem;
    margin-bottom: 0.25rem;
    max-height: none;
    overflow: visible;
    padding-right: 0;
  }

  .route-card {
    position: relative;
    display: block;
    text-align: left;
    width: 100%;
    aspect-ratio: 16 / 9;
    height: auto;
    min-height: 0;
    background-color: rgba(var(--theme-color-rgb), 0.22);
    border: 2px solid transparent;
    border-radius: 0.85rem;
    color: var(--popup-text-color);
    cursor: pointer;
    overflow: hidden;
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.28);
    transition:
      border-color 0.2s ease,
      background-color 0.2s ease,
      transform 0.2s ease,
      box-shadow 0.2s ease;
  }

  .route-card:hover {
    transform: translateY(-1px);
    background-color: rgba(var(--theme-color-rgb), 0.3);
    box-shadow: 0 14px 26px rgba(0, 0, 0, 0.35);
  }

  .route-card:focus-visible {
    outline: 3px solid var(--focus-color);
    outline-offset: 2px;
  }

  .route-image,
  .route-image-placeholder {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  .route-image {
    object-fit: cover;
    transform: scale(1);
    transition: transform 0.35s ease;
  }

  .route-card:hover .route-image,
  .route-card:focus-within .route-image {
    transform: scale(1.018);
  }

  .route-image-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
      145deg,
      rgba(var(--theme-color-rgb), 0.55),
      rgba(0, 0, 0, 0.65)
    );
    font-family: var(--font-body), sans-serif;
    font-size: 0.95rem;
    opacity: 0.9;
  }

  .route-card-content {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: auto;
    z-index: 2;
    padding: 1.2rem 0.85rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 0.35rem;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.96) 0%,
      rgba(0, 0, 0, 0.9) 22%,
      rgba(0, 0, 0, 0.74) 48%,
      rgba(0, 0, 0, 0.5) 68%,
      rgba(0, 0, 0, 0.26) 84%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  .route-title {
    margin: 0;
    font-family: var(--font-heading), sans-serif;
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1.2;
    color: #ffffff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  }

  .route-description {
    margin: 0;
    font-family: var(--font-body), sans-serif;
    font-size: 0.88rem;
    line-height: 1.45;
    white-space: pre-wrap;
    color: #ffffff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transform: translateY(4px);
    transition: max-height 0.25s ease, opacity 0.25s ease, transform 0.25s ease;
  }

  .route-card:hover .route-description,
  .route-card:focus-within .route-description {
    max-height: 15rem;
    opacity: 0.96;
    transform: translateY(0);
  }

  .route-start-button {
    align-self: flex-start;
    margin-top: 0.55rem;
    appearance: none;
    border: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 999px;
    background: rgba(var(--theme-color-rgb), 0.98);
    color: #ffffff;
    font-family: var(--font-heading), sans-serif;
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1;
    padding: 0.55rem 0.9rem;
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.32);
    transition: background-color 0.2s ease, transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease;
  }

  .route-start-button:hover {
    background: rgba(var(--theme-color-rgb), 1);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.38);
  }

  .route-start-button:focus-visible {
    outline: 3px solid var(--focus-color);
    outline-offset: 2px;
  }

  .route-start-button-hover {
    display: none;
    opacity: 0;
    pointer-events: none;
    margin-top: 0;
  }

  .route-card:hover .route-start-button-hover,
  .route-card:focus-within .route-start-button-hover {
    display: inline-flex;
    opacity: 1;
    pointer-events: auto;
    margin-top: 0.55rem;
  }

  .route-start-button-always {
    opacity: 1;
    pointer-events: auto;
  }

  .route-card-all {
    background: linear-gradient(
      145deg,
      rgba(20, 20, 20, 0.86),
      rgba(48, 48, 48, 0.88)
    );
    border-color: rgba(255, 255, 255, 0.22);
  }

  .route-card-all .route-card-content {
    top: 0;
    height: 100%;
    justify-content: center;
    background: none;
    padding: 0 1rem;
  }

  .route-description-always {
    max-height: 15rem;
    opacity: 0.96;
    transform: translateY(0);
  }

  @media (min-width: 768px) {
    .intro-popup.has-routes {
      display: grid;
      grid-template-rows: minmax(0, 1fr) auto;
      row-gap: 1.25rem;
      align-items: stretch;
      min-height: 0;
    }

    .intro-popup.has-routes .intro-main {
      grid-row: 1;
      min-height: auto;
      max-height: none;
      overflow: visible;
      padding-right: 0;
    }

    .intro-popup.has-routes .intro-routes {
      grid-row: 2;
      display: flex;
      flex-direction: column;
    }

    .intro-popup.has-routes .intro-post-content {
      grid-row: 3;
    }

    .routes-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      grid-auto-rows: auto;
      max-height: none;
      min-height: auto;
    }
  }
</style>
